import React from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Shield, FileText, Cookie, Lock, Bot, Server, Trash2 } from 'lucide-react';
import SEO from '../components/SEO';

/**
 * Legal pages: Privacy, Terms, Security, Cookies.
 *
 * These documents are deliberately kept in English only. They are the
 * authoritative text; machine-translating binding legal wording into 21 further
 * languages would create more exposure than it removes. The rest of the product
 * remains fully localised.
 *
 * Every factual claim below was verified against the running system before
 * being written. The previous version of this file asserted SOC 2 Type II
 * certification, an active bug bounty programme, AES-256 encryption at rest for
 * all data, and ownership by "BusyBee, Inc." - none of which were true.
 */

const EFFECTIVE_DATE = 'October 31, 2026';

const COMPANY = {
  name: 'NeoPixel s.r.o.',
  address: 'Wuppertálska 57, 040 23 Košice, Slovak Republic',
  ico: '47079908',
  dic: '2023793387',
  vat: 'SK2023793387',
  register:
    'Commercial Register of the Municipal Court Košice, Section: Sro, Insert No. 32572/V',
};

const IDENTITY_LINES = [
  `${COMPANY.name}, ${COMPANY.address}`,
  `Company ID (IČO): ${COMPANY.ico} · Tax ID (DIČ): ${COMPANY.dic} · VAT ID (IČ DPH): ${COMPANY.vat}`,
  COMPANY.register,
  'Contact: via the contact form at busybee.day/contact, or by post at the address above.',
];

const legalContent = {
  privacy: {
    title: 'Privacy Policy',
    icon: Lock,
    sections: [
      {
        heading: '1. Who is responsible for your data',
        content:
          'BusyBee.Day is operated by NeoPixel s.r.o., which is the data controller for the personal data described in this policy. We have not appointed a Data Protection Officer, as we are not required to under Article 37 GDPR. You can reach a human at the addresses below.',
        list: IDENTITY_LINES,
      },
      {
        heading: '2. What we collect',
        content: 'We collect only what the service needs in order to work:',
        list: [
          'Account data: your email address, name, password (stored only as a bcrypt hash), language and theme preference, and - if you enable two-factor authentication - an encrypted TOTP secret. If you sign in with Google, we also receive your Google account ID, email address, name and profile picture.',
          'Content you create: tasks, projects, comments, documents, whiteboards, mindmaps, databases, habits, labels, and any files you attach.',
          'Bot conversations and work: the messages you send to bots, the tasks and comments you hand them, what they produce (answers, documents, files, tasks), the steps of each bot run (which tools were used and what came back) and, when a bot uses its computer, screenshots of that computer.',
          'Bot memory: short facts a bot keeps about a project, about how you like the work done in it, and a profile of how you like to work in general, plus the skills and rules your projects teach bots. You can read, change and delete every entry.',
          'Connected services: when you connect a service to a bot (for example Gmail, Google Calendar, Notion, ClickUp, Canva or Stripe), the sign-in tokens or keys for it, stored encrypted, and the data the bot reads or writes there while doing what you asked.',
          'Your own AI keys, if you add them: stored encrypted and used only for your requests.',
          'If you sign in with Apple (iPhone app): your Apple ID and the e-mail address you choose to share, which may be a private relay address. We keep a token from Apple only to end that sign-in when you delete your account.',
          'The iPhone and Android apps: a push token for each phone where you turned notifications on (removed when you sign out or delete the account), and for purchases in the app, what you bought and when, which Apple or Google confirm through RevenueCat. Apple or Google take the payment; we never see card details.',
          'Team data: which teams and projects you belong to, your role, and activity logs showing who changed what.',
          'Usage and technical data: sign-in timestamps, IP address and user agent at authentication, the credits each bot run used and on which model, and server logs kept for security and debugging.',
          'How the app is used: the days you used it and from which app (web, desktop, iPhone or Android); at sign-up, the country, the language, the site that sent you and the campaign name in the link, if any. Your IP address is used at sign-up only as a one-way hash, kept for a day, to stop mass sign-ups.',
          'Error reports from the app: when something breaks, the app sends the error, where in the app it happened, the page path, the app version and your account ID. Never the content you work on.',
          'Visits to busybee.day: Cloudflare Web Analytics counts visits without cookies and without storing anything on your device - the page, the referring site, the country, the browser type and loading speed, in aggregate. It does not identify you or follow you across sites.',
          'Billing data: your plan, subscription status, billing period, credit purchases and the invoices we issue. Card details are entered directly with Stripe and never reach our servers.',
          'Integration data, only if you connect it yourself: Google Calendar events, Gmail messages, or content you import from Notion or ClickUp.',
        ],
      },
      {
        heading: '3. Why we process it, and on what legal basis',
        content:
          'Article 6 GDPR requires a lawful basis for each purpose. Ours are:',
        list: [
          'Providing the service, your account and your workspace - performance of a contract, Art. 6(1)(b).',
          'Running the bots you start, including their memory, computer, web search and the services you connect to them - performance of a contract, Art. 6(1)(b), since every bot run starts from your message, a task you hand over or a schedule you set.',
          'Counting the credits your bots use and billing them - performance of a contract and legal obligation, Art. 6(1)(b) and (c).',
          'Billing, invoicing and statutory accounting records - performance of a contract and legal obligation, Art. 6(1)(b) and (c).',
          'Transactional email such as sign-in links, password resets, security alerts and notices from your bots - performance of a contract, Art. 6(1)(b).',
          'Keeping the service secure, preventing abuse of bots and computers, and debugging faults - legitimate interests, Art. 6(1)(f).',
          'Understanding how the website and the app are used so we can improve them (visit statistics, which features are used, where sign-ups come from and where people get stuck) - legitimate interests, Art. 6(1)(f).',
          'Connecting Google Calendar, Gmail, Notion, ClickUp or another service - your consent, Art. 6(1)(a), which you can withdraw at any time by disconnecting it.',
        ],
      },
      {
        heading: '4. How AI processing works',
        content:
          'Bots and the AI features send text to an AI model to produce an answer. This is what happens to it:',
        list: [
          'We send the model only what the work needs: your instructions, the task, document or chat in question, the relevant memory, and the results of the tools the bot used.',
          'Requests go through OpenRouter, Inc. to the provider of the model you or your bot use. We instruct OpenRouter to route them only to providers that do not store or train on the data they receive (its "data_collection: deny" setting), and OpenRouter itself does not store prompts unless asked to.',
          'We do not use your content to train AI models, and the providers we route to may not either.',
          'If you add your own AI key, requests on that key go directly to that provider under your own agreement with it.',
          'Some quick checks during bot work go to Cloudflare Workers AI instead of a language model: whether the screen of a bot\'s computer shows a sign-in, a CAPTCHA, a payment form, an error or finished work; whether outside content holds instructions aimed at AI; and whether a finished run has anything worth learning. Cloudflare does not use this content to train models or to improve its services.',
          'When a bot searches the web, the search query goes to Perplexity or Brave Search. Pages a bot reads are fetched from the public web.',
          'Dictation: in Chrome, Edge, Safari and the iPhone and Android apps, the browser or the phone turns speech into text itself (through Google, Microsoft or Apple). In a browser that cannot (such as Brave, Vivaldi or Firefox), the recording of what you say goes the same way as text, through OpenRouter, to a model that writes it down. We do not keep the recording.',
          'A bot computer is an isolated cloud machine run by FoundryLabs, Inc. (E2B) for one project. It pauses after 5 minutes without use. Files on it stay until the project\'s computer is deleted.',
          'Content that comes from outside BusyBee.Day - emails, web pages, connected services - is treated as untrusted: after a bot has read it, changes to its memory need your approval, and by default actions that change something in a connected service wait for your approval.',
          'Bots are AI systems. Their messages, files and changes are marked as coming from a bot, so you always know what an AI produced.',
        ],
      },
      {
        heading: '5. How long we keep it',
        content: 'Retention is tied to purpose, not kept open-ended:',
        list: [
          'Account and content data: for as long as your account exists. When you delete your account it closes at once and its data is removed for good 30 days later; work you did inside a team stays with that team (see busybee.day/delete-account).',
          'Bot chats, runs and the files bots made: for as long as the chat or project exists. Screenshots of a bot computer are kept with the run and may be deleted earlier to save space.',
          'Bot memory, skills and rules: until you or a bot remove them, or the project is deleted.',
          'Tokens and keys for connected services and your own AI keys: until you disconnect or remove them, or delete your account.',
          'Items you move to Trash: retained for the retention window shown in the app, then purged automatically.',
          'Accounting and invoicing records: 10 years, as required by Slovak Act No. 431/2002 Coll. on Accounting.',
          'Server and security logs: up to 12 months.',
        ],
      },
      {
        heading: '6. Who else processes your data',
        content:
          'We do not sell your data and we do not use it for advertising. The companies that process data for us, under data processing agreements, are listed on our Subprocessors page (busybee.day/subprocessors): hosting, file storage, email, payments, AI routing and models, web search and bot computers. Services you connect to a bot yourself are not our processors - the bot accesses them on your instruction, and their own terms and privacy policies apply.',
      },
      {
        heading: '7. Transfers outside the EU',
        content:
          'Your account data, content and attachments are hosted inside the EU. Several processors - among them the AI model providers, OpenRouter, E2B and the web search providers - are established in the United States or other countries outside the EU. Where personal data reaches them, the transfer relies on the European Commission\'s Standard Contractual Clauses and, where applicable, the EU-US Data Privacy Framework. You can ask us for a copy of the safeguards in place.',
      },
      {
        heading: '8. Your rights',
        content:
          'Under the GDPR you have the right to access your data, to have it corrected, to have it erased, to restrict or object to processing, to data portability, and to withdraw consent at any time without affecting processing already carried out. Most of these you can exercise yourself: export or delete your data in Settings, delete bot memory entries, disconnect services, and delete chats or projects. For anything else contact us through busybee.day/contact and we will respond within one month.',
      },
      {
        heading: '9. Complaining to a supervisory authority',
        content:
          'If you believe we are handling your data unlawfully, you can lodge a complaint with the Slovak supervisory authority, or with the authority in your own country of residence:',
        list: [
          'Úrad na ochranu osobných údajov Slovenskej republiky',
          'Galvaniho 16130/7B, 821 04 Bratislava - Ružinov, Slovak Republic',
          'dataprotection.gov.sk',
        ],
      },
      {
        heading: '10. Automated decision-making',
        content:
          'We do not carry out automated decision-making or profiling that produces legal effects for you. Bots work on what you or your team ask them to do; they never make decisions about your account, your access or your billing.',
      },
      {
        heading: '11. Google user data',
        content:
          'BusyBee.Day\'s use and transfer of information received from Google APIs to any other app will adhere to the Google API Services User Data Policy, including the Limited Use requirements. The use of information received from Google Workspace APIs will adhere to the Google Workspace API User Data and Developer Policy, including the Limited Use requirements. In practice:',
        list: [
          'What we receive: your Google profile (name, email, picture) if you sign in with Google; calendar events and calendar lists if you connect Google Calendar; email messages, labels and drafts if you connect Gmail to the inbox feature or to a bot.',
          'What we use it for: only the features you turned on - signing you in, showing your calendar, turning emails into tasks, and letting your bot read, search, label or draft in your mailbox or calendar when you ask it to.',
          'Sharing: Google user data is sent to an AI model provider only to carry out the request you made, through the no-storage, no-training routing described in section 4. It is not sold, not used for advertising, not used for credit decisions, and not transferred to anyone else except as needed to provide these features, for security, or to comply with the law.',
          'AI training: Google user data is never used to develop, improve or train generalized AI or machine-learning models.',
          'Human access: our staff do not read your Google data unless you ask us to (for example in a support request), it is needed for security reasons, or the law requires it.',
          'Stopping it: disconnect the service in BusyBee.Day, or remove access at myaccount.google.com/permissions; we then delete the tokens we hold for it.',
        ],
      },
      {
        heading: '12. Children',
        content:
          'BusyBee.Day is not directed at children under 16. We do not knowingly collect their data. If you believe a child has created an account, contact us through busybee.day/contact and we will delete it.',
      },
      {
        heading: '13. Changes to this policy',
        content:
          'When we make material changes we will update the date above and notify account holders by email before the change takes effect.',
      },
    ],
  },

  terms: {
    title: 'Terms of Service',
    icon: FileText,
    sections: [
      {
        heading: '1. Who you are contracting with',
        content:
          'These terms form a contract between you and the operator of BusyBee.Day:',
        list: IDENTITY_LINES,
      },
      {
        heading: '2. The service',
        content:
          'BusyBee.Day is a web-based workspace with AI bots: bots that chat with you, work on tasks you hand them, use services you connect (through the Model Context Protocol, MCP), search the web and, on some plans, use a computer of their own; plus tasks and projects across list, table, board, calendar, timeline and gallery views, documents, whiteboards, mindmaps and databases, habits, focus time and planning, and team workspaces with roles. Features differ by plan, and we may add, change or retire individual features over time.',
      },
      {
        heading: '3. Your account',
        content:
          'You need an account to use BusyBee.Day, and you must be at least 16. Keep your credentials confidential; you are responsible for activity under your account, including what bots do on your instructions. Two-factor authentication is available and we recommend enabling it. Tell us promptly through busybee.day/contact if you suspect unauthorised access.',
      },
      {
        heading: '4. Plans, credits, billing and VAT',
        content:
          'BusyBee.Day offers a free plan and paid Basic, Pro, Max and Team plans, billed monthly or annually through Stripe. Paid plans renew automatically until cancelled, and you can cancel at any time from the subscription page - cancellation takes effect at the end of the paid period.',
        list: [
          'Prices include VAT where it applies. We are a Slovak VAT payer (VAT ID SK2023793387). The VAT is worked out at checkout from your billing address: a business in another EU country with a valid VAT ID pays the same price without VAT (reverse charge), and customers outside the EU pay no Slovak VAT. Every payment gets an invoice, sent by email and listed on the subscription page.',
          'Paid plans start with a 14-day free trial, once per customer. A payment card is needed to start it; when the trial ends the card is charged for the chosen plan unless you cancelled before.',
          'You can change plans on the subscription page. An upgrade takes effect at once and you pay the difference for the rest of the current period. A downgrade, or a switch from yearly to monthly billing, takes effect at the end of the paid period; the current period is not refunded.',
          'Bot work is paid in credits. One credit corresponds to about one cent of what the work really costs us (AI model tokens, web searches, computer minutes), so each model uses credits at its own price; the app shows what every run used.',
          'Each plan includes a monthly credit allowance. It renews on the first day of each month, and credits not used by then expire.',
          'You can buy extra credits. They are valid for 12 months from purchase, and credits that expire soonest are used first.',
          'When credits run out, bots stop starting new work until credits are available again. Credits have no cash value and cannot be transferred.',
        ],
      },
      {
        heading: '5. Right of withdrawal for consumers',
        content:
          'If you are a consumer in the EU you have 14 days from the start of a paid subscription, or from a credit purchase, to withdraw from the contract without giving a reason. At checkout you expressly ask for the plan or the credits to be supplied at once. If you withdraw from a subscription within the 14 days, we refund what you paid minus the part for the days already supplied, and the plan ends. Credits are digital content: you acknowledge at checkout that you lose the right to withdraw from a credit purchase once you start using the credits. To withdraw, contact us through busybee.day/contact. Outside these 14 days you can still cancel as described in section 4.',
      },
      {
        heading: '6. AI bots',
        content:
          'Bots are AI systems that act on instructions from you and your team. Because of that:',
        list: [
          'You decide what bots do. You are responsible for the instructions you give, for the actions you approve, and for any actions you allow a bot to take without asking.',
          'AI output can be wrong, incomplete or out of date, even when it sounds certain. Review answers, documents and changes before you rely on them or send them to anyone. Bots do not give legal, medical, financial or other professional advice.',
          'What a bot produces for you is yours, as described in section 9. Similar output may be produced for other users.',
          'Content from outside BusyBee.Day can try to mislead a bot. We build in safeguards such as approvals, but no safeguard is complete, so keep approvals on for actions that matter.',
          'We may limit, pause or stop bot work - for the whole service, a project or a run - to protect the service, its users or third parties.',
        ],
      },
      {
        heading: '7. Connected services',
        content:
          'When you connect a service to a bot - Gmail, Google Calendar, Notion, ClickUp, Canva, Stripe, PayPal, GitHub or any other MCP server - you authorise the bot to read and act on your account there within the permissions you grant. You confirm that you are allowed to give that access. A connection you share with your team can be used by everyone who works with that bot. The service\'s own terms continue to apply, and we are not responsible for third-party services, their availability, or changes they make. You can disconnect at any time.',
      },
      {
        heading: '8. Acceptable use',
        content: 'You agree not to use BusyBee.Day or its bots to:',
        list: [
          'do anything unlawful, or store, create or distribute unlawful content;',
          'send spam or unsolicited bulk messages, or contact people in breach of the law;',
          'impersonate anyone, or pass off AI-generated content as human-made where that misleads people;',
          'harass, threaten or harm anyone, or create sexual content involving minors;',
          'collect data from websites or services in breach of their terms or the law;',
          'run malware, attacks, cryptocurrency mining or abusive traffic on a bot computer;',
          'get around safeguards, approvals, plan limits or credit metering, or place unreasonable load on the service;',
          'break the usage policies of the AI model providers your bots use;',
          'attempt to breach, probe or disrupt the service, other accounts, or the infrastructure it runs on;',
          'resell or provide the service to third parties outside the team features it offers.',
        ],
      },
      {
        heading: '9. Your content',
        content:
          'Everything you create in BusyBee.Day, and what bots produce for you, remains yours. You grant us only the limited licence needed to host, process, back up and display it, and to send it to the processors that run the features you use, so the service can function, and to share it with the teammates and recipients you choose. We do not use it to train AI models. You can export your data or delete your account at any time.',
      },
      {
        heading: '10. Our intellectual property',
        content:
          'The BusyBee.Day software, brand, design and documentation belong to NeoPixel s.r.o. and are protected by copyright and trademark law. These terms grant you a right to use the service, not any ownership in it.',
      },
      {
        heading: '11. Availability',
        content:
          'We work to keep BusyBee.Day available and take regular backups, but we do not offer a contractual uptime guarantee on any plan. Maintenance, upgrades, incidents and outages of AI providers or connected services may cause interruptions. We will give notice of planned maintenance where it is likely to be disruptive.',
      },
      {
        heading: '12. Suspension and termination',
        content:
          'You may stop using BusyBee.Day and delete your account at any time. We may suspend or terminate an account that materially breaches these terms, or where required by law - normally after notice and an opportunity to put things right, except where the breach is serious or unlawful. On termination you may export your data for a reasonable period before deletion.',
      },
      {
        heading: '13. Liability',
        content:
          'To the extent permitted by law, we are not liable for indirect or consequential loss, lost profits, loss of data caused by factors outside our control, or for actions a bot took on your instructions or with your approval, and our total liability for any claim is limited to the fees you paid in the 12 months before the event giving rise to it. Nothing in these terms limits liability for death or personal injury caused by negligence, for fraud, or for anything else that cannot be limited under Slovak or EU law. If you are a consumer, your statutory rights are unaffected.',
      },
      {
        heading: '14. Governing law and disputes',
        content:
          'These terms are governed by the law of the Slovak Republic, and the Slovak courts have jurisdiction. If you are a consumer, this does not deprive you of the protection of mandatory rules of the country where you live, and you may bring proceedings in your own country\'s courts. Consumers may also use out-of-court dispute resolution under Act No. 391/2015 Coll.; the residual competent body is the Slovak Trade Inspection (Slovenská obchodná inšpekcia, soi.sk). The EU Online Dispute Resolution platform ceased operating on 20 July 2025 and is no longer available.',
      },
      {
        heading: '15. Changes to these terms',
        content:
          'We may update these terms as the service develops. Material changes will be notified by email before they take effect, and continuing to use BusyBee.Day afterwards means you accept them. If you do not, you may cancel.',
      },
    ],
  },

  ai: {
    title: 'AI and Bots',
    icon: Bot,
    sections: [
      {
        heading: 'What a bot is',
        content:
          'A bot in BusyBee.Day is an AI colleague that works in your projects. It answers in chat, takes over tasks you hand it, remembers what matters about a project, uses services you connect to it and, from the Pro plan, a computer of its own with a browser and a terminal. Every bot is an AI system and everything it does is marked as coming from a bot.',
      },
      {
        heading: 'Which AI models bots use',
        content:
          'Bots run on large language models from several makers, including Anthropic (Claude), OpenAI (GPT), Google (Gemini), xAI (Grok), DeepSeek, Moonshot, Z.ai, Alibaba (Qwen), MiniMax and Xiaomi. You pick the model per chat, per bot or as your default; each model uses credits at its own price, shown in the app. Requests are routed through OpenRouter only to providers that neither store nor train on what they receive.',
      },
      {
        heading: 'What we do with your data',
        list: [
          'Your content is not used to train AI models - not by us, and not by the providers we route to.',
          'A model receives only what the current piece of work needs.',
          'Tokens for connected services and your own AI keys are stored encrypted.',
          'Google user data follows the Google API Services User Data Policy, including the Limited Use requirements.',
          'The full details are in the Privacy Policy, and the companies involved are on the Subprocessors page.',
        ],
      },
      {
        heading: 'You stay in control',
        list: [
          'Actions that change something in a connected service wait for your approval by default; admins choose per tool what may run on its own.',
          'Every memory change can be undone, and after a bot has read outside content (emails, web pages) memory changes need your approval.',
          'Each run shows the steps a bot took, the tools it used and the credits it spent.',
          'Project admins can pause bots in a project or set a monthly credit budget; we can pause all bots at once if something goes wrong.',
          'A bot computer can be watched live and taken over at any moment.',
        ],
      },
      {
        heading: 'Limits worth knowing',
        content:
          'AI can be wrong while sounding sure, and content from outside can try to mislead it. Review what matters before you rely on it or send it on, and keep approvals on for actions you cannot easily undo. Bots do not give legal, medical or financial advice.',
      },
    ],
  },

  'delete-account': {
    title: 'Delete your account',
    icon: Trash2,
    sections: [
      {
        heading: 'How to delete it',
        content:
          'You can delete your BusyBee.Day account yourself, in the app for iPhone and Android or on the web. Sign in, open Settings, then Data & privacy, and choose Delete account. We e-mail you a 6-digit code; enter it to confirm.',
        list: [
          'iPhone and Android app: Settings -> Data & privacy -> Delete account.',
          'Web: busybee.day/app/settings?tab=data-privacy -> Delete account.',
          'Cannot sign in any more? Ask us through busybee.day/contact from the e-mail address of the account and we delete it for you.',
        ],
      },
      {
        heading: 'What happens',
        content: 'The account closes at once: you are signed out everywhere and nobody can sign in to it again.',
        list: [
          'A paid plan bought on the web is cancelled straight away. A subscription bought in the App Store or on Google Play is managed by Apple or Google: cancel it there so you are not charged again.',
          'Bots stop working for you and their schedules are switched off.',
          '30 days later everything personal is removed for good: your profile, tasks, projects, documents, habits, bot chats, bot memory, files, connected services and sign-in methods. Until then you can ask us through busybee.day/contact to restore the account.',
        ],
      },
      {
        heading: 'What is kept',
        content: 'Two things stay after the 30 days:',
        list: [
          'Work you did inside a team (tasks, documents, comments) belongs to that team and stays with it. Teams where you were the only member are deleted with everything in them. If you own a team with other members, make one of them the owner before you delete your account.',
          'Invoices and payment records, for 10 years, as Slovak Act No. 431/2002 Coll. on Accounting requires. They carry only an account number and amounts, not your profile.',
        ],
      },
    ],
  },

  subprocessors: {
    title: 'Subprocessors',
    icon: Server,
    sections: [
      {
        heading: 'Who processes data for BusyBee.Day',
        content:
          'These companies process personal data on behalf of NeoPixel s.r.o. to run BusyBee.Day, each under a data processing agreement or equivalent terms. Services you connect to a bot yourself are not on this list: the bot uses them on your instruction and their own terms apply.',
      },
      {
        heading: 'Infrastructure',
        list: [
          'Hetzner Online GmbH (Germany) - application, database and cache servers in Finland, EU.',
          'Cloudflare, Inc. (USA) - R2 object storage for attachments and files bots create, and Web Analytics, cookieless statistics of visits to busybee.day.',
          'Amazon Web Services EMEA SARL (Luxembourg) - Amazon SES, transactional email.',
          'Plus Five Five, Inc. (Resend, USA) - transactional email.',
          'Apple Inc. (Apple Push Notification service) and Google LLC (Firebase Cloud Messaging) - deliver notifications to the iPhone and Android apps.',
        ],
      },
      {
        heading: 'Payments',
        list: [
          'Stripe Payments Europe, Ltd. (Ireland) and Stripe, Inc. (USA) - subscriptions, credit purchases and payment processing.',
          'Apple Inc. and Google LLC - purchases in the iPhone and Android apps; they sell to you, take the payment and handle its VAT.',
          'RevenueCat, Inc. (USA) - checks purchases in the apps with Apple and Google and tells us what was bought.',
        ],
      },
      {
        heading: 'AI and bots',
        list: [
          'OpenRouter, Inc. (USA) - routes AI requests to model providers, only to those that do not store or train on the data.',
          'AI model providers selected through OpenRouter for the model in use - for example Anthropic, OpenAI, Google, xAI, Amazon Bedrock, Together AI, Mistral AI and the makers or hosts of the other models in the app. They process a request only to answer it.',
          'Cloudflare, Inc. (USA) - Workers AI, a decision model for quick checks during bot work (for example whether the bot\'s screen shows a sign-in).',
          'Perplexity AI, Inc. (USA) and Brave Software, Inc. (USA) - web search queries made by bots.',
          'FoundryLabs, Inc. (E2B, USA) - isolated cloud computers that bots use on the Pro plan and above.',
        ],
      },
      {
        heading: 'Sign-in and imports you choose',
        list: [
          'Google Ireland Ltd. / Google LLC - Google sign-in, and Google Calendar and Gmail if you connect them.',
          'Apple Inc. - Sign in with Apple in the iPhone app.',
          'Notion Labs, Inc. and ClickUp (Mango Technologies, Inc.) - only when you import from those tools.',
        ],
      },
      {
        heading: 'Changes to this list',
        content:
          'We update this page before a new subprocessor starts processing personal data. Material changes are announced to account holders by email.',
      },
    ],
  },

  security: {
    title: 'Security',
    icon: Shield,
    sections: [
      {
        heading: 'What we actually do',
        content:
          'This page describes measures that are in place today. We hold no security certifications and we do not claim any.',
        list: [
          'Passwords are stored only as bcrypt hashes. We never store or log them in plaintext and cannot recover them.',
          'Two-factor authentication (TOTP) is available on every account, and the TOTP secrets are encrypted at rest with Fernet (AES-128-CBC with HMAC-SHA256).',
          'Sessions use signed JWTs; sign-in with Google uses OAuth 2.0 with PKCE and server-side verification of the identity token.',
          'Traffic between your browser and BusyBee.Day is served over HTTPS.',
          'File attachments are stored in Cloudflare R2, which encrypts every object at rest with AES-256 and secures transfers with TLS.',
          'Application, database and cache servers are hosted with Hetzner Online GmbH in Finland, inside the EU.',
          'Sign-in tokens and keys for services connected to bots, and AI keys you add, are encrypted before they are stored.',
          'Bot computers are isolated cloud machines, one per project; bots cannot reach BusyBee.Day\'s internal network from them or through web fetching.',
          'Team access is controlled by six roles, and an activity log records who changed what.',
          'Backups are taken regularly so data can be restored after an incident.',
        ],
      },
      {
        heading: 'What we are still improving',
        content:
          'We would rather tell you this than let you assume otherwise. Our MongoDB deployment is MongoDB Community Edition, which does not offer native encryption at rest, so database contents rely on the security of the host rather than database-level encryption. Hardening the database connection and storage layer is on our roadmap. If this matters for your use case, ask us before you rely on BusyBee.Day for sensitive data.',
      },
      {
        heading: 'Reporting a vulnerability',
        content:
          'We do not run a paid bug bounty programme. We do want to hear about security problems: report them through busybee.day/contact with enough detail to reproduce the issue, and we will acknowledge it and keep you updated. Please give us a reasonable opportunity to fix it before disclosing it publicly.',
      },
    ],
  },

  cookies: {
    title: 'Cookie Policy',
    icon: Cookie,
    sections: [
      {
        heading: 'We do not use cookies',
        content:
          'BusyBee.Day sets no cookies at all. Our servers never send a Set-Cookie header. We run no advertising pixels and no third-party trackers - there is no Google Analytics, no Tag Manager, no Meta pixel, and nothing comparable. To count visits we use Cloudflare Web Analytics, which sets no cookies, stores nothing on your device and does not identify or follow you.',
      },
      {
        heading: 'What we do store in your browser',
        content:
          'The app keeps some data in your browser\'s local and session storage so it can work. Under the ePrivacy rules this is treated the same way as cookies, so here is what it is:',
        list: [
          'token - your sign-in session. Without it you would have to sign in on every page.',
          'Preferences you set: interface language (bb_language) and light or dark mode (bb_theme).',
          'Interface state: which sidebar sections are open, the layout of the task view, the last project and panel you used with each bot, and how you arranged database views.',
          'Work in progress kept on your device: unsent drafts, your recent searches and your whiteboard shape library.',
          'Short-lived values during sign-in and when connecting a service to a bot (oauth_intent, bb.connect.return), removed straight afterwards.',
        ],
      },
      {
        heading: 'Why there is no consent banner',
        content:
          'Consent is required for storage on your device that is not strictly necessary, such as advertising and tracking. We have none: the entries above exist only to deliver the service you asked for and to remember preferences you set yourself, and our visit statistics store nothing on your device. So no banner is shown. If we ever add anything that needs your consent, we will ask first and nothing will load before you give it.',
      },
      {
        heading: 'Clearing it',
        content:
          'Signing out removes your session. You can clear the rest at any time through your browser\'s site-data settings; the app will simply fall back to its defaults.',
      },
    ],
  },
};

const CANONICAL = { ai: '/ai-policy' };

const LegalPage = () => {
  const { t } = useTranslation('landing');
  const { pageId } = useParams();
  const location = useLocation();

  // Determine pageId from path if not provided via params
  let activePage = pageId;
  if (!activePage) {
    const path = location.pathname;
    if (path === '/privacy') activePage = 'privacy';
    else if (path === '/terms') activePage = 'terms';
    else if (path === '/security') activePage = 'security';
    else if (path === '/cookies') activePage = 'cookies';
    else if (path === '/ai-policy') activePage = 'ai';
    else if (path === '/subprocessors') activePage = 'subprocessors';
    else if (path === '/delete-account') activePage = 'delete-account';
    else activePage = 'privacy';
  }

  const page = legalContent[activePage] || legalContent.privacy;
  const Icon = page.icon;

  const seoMeta = {
    privacy: { title: 'Privacy Policy: how we handle your data', description: 'How BusyBee.Day collects, uses, stores and shares personal data, and the rights you have under the GDPR.' },
    terms: { title: 'Terms of Service for BusyBee.Day', description: 'The terms governing your use of BusyBee.Day, operated by NeoPixel s.r.o.' },
    security: { title: 'Security: how BusyBee.Day protects your data', description: 'The security measures protecting BusyBee.Day accounts and data, and what we are still improving.' },
    cookies: { title: 'Cookie Policy: no cookies, no trackers', description: 'BusyBee.Day uses no cookies and no trackers. What the app stores in your browser, and why there is no consent banner.' },
    ai: { title: 'AI and Bots: models, training and approvals', description: 'How BusyBee.Day bots work, which AI models they use, why your content is not used to train AI, and how approvals keep you in control.' },
    'delete-account': { title: 'Delete your account', description: 'How to delete your BusyBee.Day account in the app or on the web, what is removed and what is kept.' },
    subprocessors: { title: 'Subprocessors: who processes data for us', description: 'The companies that process personal data for BusyBee.Day: hosting, storage, email, payments, AI routing and models, web search and bot computers.' },
  };
  const seo = seoMeta[activePage] || seoMeta.privacy;

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <SEO title={seo.title} description={seo.description} canonical={CANONICAL[activePage] || `/${activePage}`} englishOnly />
      <Navbar />

      <main className="pt-16 sm:pt-20 pb-16 sm:pb-24">
        <article className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
          <header className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">{page.title}</h1>
              <p className="text-gray-500 text-xs sm:text-sm">{t('legal.last_updated')}: {EFFECTIVE_DATE}</p>
            </div>
          </header>

          <div className="space-y-6 sm:space-y-8">
            {page.sections.map((section, i) => (
              <section key={i} className="bg-white/5 rounded-xl border border-gray-800 p-5 sm:p-6">
                <h2 className="text-lg sm:text-xl font-semibold text-white mb-3">{section.heading}</h2>
                {section.content && (
                  <p className="text-sm sm:text-base text-gray-400 leading-relaxed">{section.content}</p>
                )}
                {section.list && (
                  <ul className="mt-3 space-y-2 text-sm sm:text-base text-gray-400 leading-relaxed">
                    {section.list.map((item, j) => (
                      <li key={j} className="flex gap-2">
                        <span className="text-amber-500/60 flex-shrink-0" aria-hidden="true">·</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          <p className="mt-8 text-xs text-gray-600 leading-relaxed">
            This document is published in English, which is the authoritative version.
          </p>

          <aside className="mt-10 sm:mt-12 p-5 sm:p-6 bg-gradient-to-br from-amber-900/20 to-orange-900/20 rounded-2xl border border-gray-800">
            <h3 className="text-white font-semibold mb-2">{t('legal.questions.headline')}</h3>
            <p className="text-sm sm:text-base text-gray-400 mb-4">{t('legal.questions.body')}</p>
            <Link to="/contact" className="text-amber-400 hover:text-amber-300 font-medium">{t('legal.questions.cta')}</Link>
          </aside>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default LegalPage;
